import React, { useState, useRef, useEffect } from 'react'; // Added useRef and useEffect
import { Link, useLocation } from 'react-router-dom';
// Include ChevronDown
import { LayoutDashboard, MessageSquare, Users, Settings, TruckElectric, Building2, ReceiptText, Package, Crown, Tag, ChartArea, ShoppingCart, ChevronDown, Wrench, Wallet } from 'lucide-react';
import Button from '../ui/Button';
import logo_1024_X_1024 from '../../assets/logo_1024_X_1024.png'

/**
 * Sidebar Component for navigation.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.isActive - Controls whether the sidebar is active (expanded) or collapsed.
 */
const Sidebar = ({ isActive, onClose }) => {
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState(null);
    const [expandedSublist, setExpandedSublist] = useState(null);

    // Ref to hold the position of the link when collapsed
    const linkRefs = useRef({});
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

    // Define navigation items (unchanged)
    const navItems = [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Chat', path: '/chat', icon: MessageSquare },
        // { name: 'Customers', path: '/customers', icon: Users },
        { name: 'Branches', path: '/branches', icon: Building2 },
        { 
            name: 'Accounts', 
            path: '/accounting', 
            icon: ReceiptText,
            subItems: [
                { name: 'Chart of Accounts', path: '/accounting/coa' },
                { name: 'Transactions History', path: '/accounting/transactions' },
                { name: 'Asset Management', path: '/accounting/assets' },
            ]
        },
        {
            name: 'Wallet',
            path: '/wallet',
            icon:  Wallet,
            subItems: [
                { name: 'Virtual Wallet', path: '/wallet/virtual-wallet' },
                { name: 'Payroll Management', path: '/wallet/payroll-management' },
                { name: 'Wallet Expenses', path: '/wallet/wallet-expenses'}
            ]
        },
        {
            name: 'Jobs',
            path: '/jobs',
            icon: Wrench,
            subItems: [
                { name: 'Production Orders', path: '/jobs/all-jobs' },
                { name: 'Start New Job', path: '/jobs/new-workflow' },
            ]
        },
        {
            name: 'Sales',
            path: '/sales',
            icon: ShoppingCart,
            subItems: [
                { name: 'Point of Sale', path: '/sales/pos'},
                { name: 'Customers', path: '/sales/customers'},
                { name: 'Discounts', path: '/sales/discounts' },
                { name: 'Quotations', path: '/sales/quotation' },
                { name: 'Invoices', path: '/sales/invoices' },
                { name: 'Sales Receipts', path: '/sales/receipts' },
                // { name: 'Customer Returns', path: '/sales/returns' },
                { name: 'Payments Received', path: '/sales/payments' },
            ]
        },
        { name: 'Catalog', path: '/catalog', icon: Tag },
        { 
            name: 'Vendor', 
            path: '/vendor', 
            icon: TruckElectric,
            subItems: [
                { name: 'Vendor', path:'/vendor/list' },
                { name: 'Quick Restock', path:'/vendor/fast-restock' },
            ]
        },
        { 
            name: 'Inventory', 
            path: '/inventory', 
            icon: Package,
            subItems: [
                { name: 'Inventory', path: '/inventory/index'},
                // { name: 'Inventory Lots', path: '/inventory/lots'},
                // { name: 'Stock Adjustment', path: '/inventory/stock-adj' },
                { name: 'Inventory Transfer', path: '/inventory/inventory-transfer' },
            ]
        },
        { name: 'Reports', path: '/reports', icon: ChartArea },
        { name: 'Settings', path: '/settings', icon: Settings },
    ];

    const sidebarWidthClasses = isActive ? 'w-64' : 'w-0 md:w-[72px] overflow-hidden md:overflow-visible';

    const collapsedWidth = 'w-[72px]';
    const expandedWidth = 'w-64';

    // Handler for the parent link click
    const handleLinkClick = (e, item) => {
        const hasSubItems = !!item.subItems;

        if (isActive && hasSubItems) {
            e.preventDefault();
            setExpandedSublist(item.name === expandedSublist ? null : item.name);
        } else if (!isActive && hasSubItems) {
            e.preventDefault();
            // Toggle the floating dropdown
            setOpenDropdown(item.name === openDropdown ? null : item.name);
        } else {
            // it's a leaf parent link (e.g. dashboard, chat, reports, settings)
            if (onClose) onClose();
        }
    };

    // Calculate the position of the popover when the item is hovered/clicked in the collapsed state
    const updatePopoverPosition = (itemName) => {
        const ref = linkRefs.current[itemName];
        if (ref) {
            const rect = ref.getBoundingClientRect();
            setPopoverPosition({
                // Popover opens flush with the right edge of the sidebar + a small margin (72px + 8px)
                left: rect.right + 8, // Collapsed sidebar width is 72px. 
                // Popover aligns vertically with the link's top edge
                top: rect.top,
            });
        }
    };

    // Close floating dropdown on mouse leave 
    const handleMouseLeave = () => {
        if (!isActive) {
            setOpenDropdown(null);
        }
    }

    // Effect to recalculate position if the window resizes or the sidebar state changes
    useEffect(() => {
        if (!isActive && openDropdown) {
            updatePopoverPosition(openDropdown);
        }
    }, [isActive, openDropdown]);


    return (
        // KEY FIX 1: Removed `overflow-hidden` from the main nav container 
        // to allow the popover to extend out without being clipped.
        <nav
            className={`flex flex-col h-screen bg-white shadow-lg border-r border-gray-medium
                  ${sidebarWidthClasses}
                  transition-all duration-300 ease-in-out
                  flex-shrink-0 relative z-50`}
            onMouseLeave={handleMouseLeave}
        >
            {/* Sidebar Header with Logo (unchanged) */}
            <div className={`flex items-center ${isActive ? 'justify-start px-4' : 'justify-center'} py-4`}>
                <img
                    src={logo_1024_X_1024}
                    alt="Arxis Logo"
                    className={`object-contain rounded-lg select-none pointer-events-none
                                ${isActive ? 'w-8 h-8 mr-3' : 'w-10 h-10'}`}
                    onContextMenu={(e) => e.preventDefault()}
                    draggable="false"
                />
                {isActive && <h3 className="text-2xl font-bold text-text-primary">Arxis</h3>}
            </div>

            {/* Navigation Links */}
            <ul className="flex-1 list-none p-0 m-0 overflow-y-auto custom-scrollbar">
                {/* Category Label (unchanged) */}
                {isActive && <p className="text-sm font-semibold text-text-secondary px-4 pt-4 pb-2">Navigation</p>}

                {navItems.map((item) => {
                    // ... (Activity checks remain the same)
                    const isParentActive = location.pathname.startsWith(item.path);
                    const isActiveLink = item.subItems
                        ? isParentActive || item.subItems.some(sub => location.pathname.startsWith(sub.path))
                        : isParentActive;

                    const IconComponent = item.icon;
                    const hasSubItems = !!item.subItems;

                    const isSublistVisible = expandedSublist === item.name;
                    const isDropdownVisible = openDropdown === item.name;

                    return (
                        <React.Fragment key={item.name}>
                            <li
                                className="my-1 relative"
                                onMouseEnter={() => {
                                    if (!isActive && hasSubItems) {
                                        setOpenDropdown(item.name);
                                        updatePopoverPosition(item.name); // Calculate position on enter
                                    }
                                }}
                            >
                                <Link
                                    to={item.path}
                                    onClick={(e) => handleLinkClick(e, item)}
                                    // KEY FIX 2: Store a ref to the link element
                                    ref={el => linkRefs.current[item.name] = el}
                                    className={`flex py-2 rounded-lg mx-2
                                    ${isActiveLink ? 'bg-gmail-blue text-white shadow-md' : 'text-text-primary hover:bg-gray-light'}
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 ${isActiveLink ? 'focus:ring-gmail-blue' : 'focus:ring-gray-dark'}
                                    transition-all duration-200 ease-in-out
                                    ${isActive
                                            ? 'px-4 items-center'
                                            : 'flex-col justify-center px-1 py-3 text-center'
                                        }
                                    `}
                                    title={isActive ? '' : item.name}
                                >
                                    {/* Icon and Text rendering (unchanged) */}
                                    <IconComponent
                                        size={20}
                                        className={`${isActive ? 'mr-3' : 'mx-auto'}`}
                                    />
                                    {isActive
                                        ? <span className="whitespace-nowrap flex-1">{item.name}</span>
                                        : (
                                            <span className="text-[10px] block mt-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                                                {item.name}
                                            </span>
                                        )
                                    }

                                    {/* Caret icon (unchanged in logic) */}
                                    {isActive && hasSubItems && (
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform duration-200 ml-auto
                                                ${isActiveLink ? 'text-white' : 'text-text-secondary'}
                                                ${isSublistVisible ? 'rotate-180' : 'rotate-0'}
                                            `}
                                        />
                                    )}
                                </Link>

                                {/* KEY FIX 3: RENDER THE POPOVER OUTSIDE THE SIDEBAR FLOW 
                                    We move the collapsed popover rendering to a fixed position at the window level.
                                    We will render the popover component only once outside the map loop below.
                                */}
                            </li>

                            {/* EXPANDED State Sublist (Inline/Indented - Unchanged) */}
                            {isActive && hasSubItems && isSublistVisible && (
                                <ul className="list-none p-0 m-0">
                                    {item.subItems.map((subItem) => (
                                        <li key={subItem.name} className="my-1">
                                            <Link
                                                to={subItem.path}
                                                onClick={() => {
                                                    if (onClose) onClose();
                                                }}
                                                className={`flex items-center py-2 rounded-lg mx-2 pl-10 text-sm
                                                ${location.pathname.startsWith(subItem.path)
                                                        ? 'bg-gmail-blue/10 text-gmail-blue font-semibold'
                                                        : 'text-text-secondary hover:bg-gray-light'
                                                    }
                                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue
                                                transition-colors duration-200 ease-in-out
                                                `}
                                            >
                                                <span className="w-1 h-1 rounded-full mr-2" style={{ backgroundColor: location.pathname.startsWith(subItem.path) ? 'var(--color-gmail-blue)' : 'currentColor' }}></span>
                                                <span className="whitespace-nowrap">{subItem.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </React.Fragment>
                    );
                })}
            </ul>

            {/* Go Pro Section (Unchanged) */}
            <div className={`p-4 ${isActive ? 'block' : 'hidden'} border-t border-gray-medium mt-auto`}>
                <div className="bg-gradient-to-br from-gmail-blue to-blue-400 p-4 rounded-xl shadow-md text-white text-center">
                    <Crown size={32} className="mx-auto mb-2 text-gmail-yellow" />
                    {/* <strong className="block text-lg font-bold mb-1">Go Pro!</strong> */}
                    {/* <span className="block text-sm mb-3">Unlock unlimited productivity.</span> */}
                    {/* <Button variant="secondary" className="bg-white text-gmail-blue hover:bg-gray-100 focus:ring-white">
                        Enable AI
                    </Button> */}
                </div>
            </div>
            <div className={`p-2 ${isActive ? 'hidden' : 'block'} border-t border-gray-medium mt-auto text-center`}>
                <Button variant="ghost" className="p-0 h-auto w-auto focus:ring-gmail-blue">
                    <Crown size={24} className="mx-auto text-gmail-yellow" title="Go Pro!" />
                </Button>
            </div>

            {/* KEY FIX 4: COLLAPSED State Dropdown Popover (FIXED/Viewport-relative)
                Rendered outside the map loop, fixed to the viewport, and controlled by state.
            */}
            {openDropdown && !isActive && (
                <div
                    className="fixed w-48 bg-white rounded-lg shadow-xl z-[9999] border border-gray-medium"
                    style={{
                        top: popoverPosition.top,
                        left: popoverPosition.left
                    }}
                >
                    <div className="py-1">
                        {navItems.find(item => item.name === openDropdown)?.subItems.map((subItem) => (
                            <Link
                                key={subItem.name}
                                to={subItem.path}
                                className={`block px-4 py-2 text-sm whitespace-nowrap
                                    ${location.pathname.startsWith(subItem.path)
                                        ? 'bg-gray-light text-gmail-blue font-semibold'
                                        : 'text-text-primary hover:bg-gray-light'
                                    }
                                    transition-colors duration-150 ease-in-out
                                `}
                                onClick={() => {
                                    setOpenDropdown(null);
                                    if (onClose) onClose();
                                }} // Close on click
                            >
                                {subItem.name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Sidebar;